import { Link } from "react-router-dom";
import { 
  Heart, 
  Eye, 
  Award,
  Users,
  Shield,
  Handshake,
  CheckCircle,
  Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";

const certifications = [
  "Joint Commission Accredited",
  "CLIA Certified Laboratory",
  "American College of Emergency Physicians",
  "North Carolina & Wyoming Medical Board Licensed",
  "HIPAA Compliant Facility",
  "Urgent Care Association Member",
  "Medicare & Medicaid Certified",
  "Top Rated by Patients (4.9/5)",
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 miami-grid relative">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#ff00ff]/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00f0ff]/20 rounded-full blur-[150px]" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">About Us</span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mt-4 mb-6">
              About <span className="text-gradient">HealthCentrix</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Committed to providing exceptional medical care with compassion, integrity, and excellence since 2005.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#00f0ff] text-sm font-mono uppercase tracking-widest">Our Story</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
                Two Decades of Excellence
              </h2>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  Founded in 2005, HealthCentrix LLC began with a simple mission: to provide accessible, high-quality medical care to communities across the nation. What started as a small urgent care clinic has grown into a comprehensive multi-specialty medical center with offices in Raleigh, NC and headquarters in Casper, WY.
                </p>
                <p>
                  Today, we serve thousands of patients annually, offering everything from emergency care to specialized treatment across 15+ medical specialties. Our team of over 50 board-certified physicians and healthcare professionals is dedicated to your well-being.
                </p>
                <p>
                  We believe in treating the whole person, not just symptoms. Our patient-centered approach combines advanced medical technology with compassionate care to deliver the best possible health outcomes.
                </p>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1516841273335-e39b37888115?w=600"
                alt="Medical team"
                className="rounded-3xl w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-[#0f0518] via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 lg:py-28 bg-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: "Our Mission",
                description: "To deliver exceptional, patient-centered healthcare that improves lives and strengthens communities through compassionate medical excellence.",
                color: "#00f0ff",
              },
              {
                icon: Eye,
                title: "Our Vision",
                description: "To be the nation's most trusted multi-specialty medical center, recognized for clinical excellence, innovation, and unwavering commitment to patient care.",
                color: "#ff00ff",
              },
              {
                icon: Award,
                title: "Our Values",
                description: "Compassion, Integrity, Excellence, Respect, and Collaboration guide everything we do in our commitment to your health and well-being.",
                color: "#ff9e00",
              },
            ].map((item, index) => (
              <div
                key={index}
                data-testid={`value-card-${index}`}
                className="value-card"
                style={{ "--value-color": item.color }}
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: `${item.color}15` }}
                >
                  <item.icon className="w-8 h-8" style={{ color: item.color }} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{item.title}</h3>
                <p className="text-gray-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Stand For */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Our Principles</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4">
              What We Stand For
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Heart,
                title: "Compassionate Care",
                description: "We treat every patient with empathy, dignity, and respect, understanding that behind every medical case is a person who deserves the highest quality of care.",
                color: "#ff00ff",
              },
              {
                icon: Award,
                title: "Clinical Excellence",
                description: "Our board-certified physicians and healthcare professionals maintain the highest standards of medical practice through continuous education and evidence-based medicine.",
                color: "#00f0ff",
              },
              {
                icon: Users,
                title: "Patient-Centered Approach",
                description: "Your health goals and preferences guide our care decisions. We believe in collaborative treatment planning and keeping you informed every step of the way.",
                color: "#ff9e00",
              },
              {
                icon: Shield,
                title: "Quality & Safety",
                description: "We maintain rigorous quality control standards and safety protocols to ensure the best possible outcomes and minimize risks in all aspects of care.",
                color: "#00f0ff",
              },
            ].map((item, index) => (
              <div
                key={index}
                data-testid={`principle-card-${index}`}
                className="glass rounded-2xl p-8 card-hover"
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${item.color}15` }}
                >
                  <item.icon className="w-7 h-7" style={{ color: item.color }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 lg:py-28 bg-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#00f0ff] text-sm font-mono uppercase tracking-widest">Recognition</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-4">
              Certifications & Accreditations
            </h2>
            <p className="text-gray-400">Recognized for excellence in healthcare delivery and patient safety</p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {certifications.map((cert, index) => (
              <div
                key={index}
                data-testid={`cert-badge-${index}`}
                className="cert-badge flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4 text-[#00f0ff]" />
                <span>{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: "20+", label: "Years of Service", color: "#00f0ff" },
              { value: "50,000+", label: "Patients Treated", color: "#ff00ff" },
              { value: "50+", label: "Medical Professionals", color: "#ff9e00" },
              { value: "15+", label: "Medical Specialties", color: "#00f0ff" },
            ].map((stat, index) => (
              <div 
                key={index}
                data-testid={`about-stat-${index}`}
                className="text-center"
              >
                <div 
                  className="text-5xl md:text-6xl font-bold mb-2"
                  style={{ color: stat.color }}
                >
                  {stat.value}
                </div>
                <div className="text-gray-400 uppercase tracking-wider text-sm">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-[#00f0ff]/10 to-[#ff00ff]/10 relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00f0ff] to-[#ff00ff]" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Join Our HealthCentrix Family
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Experience the difference of patient-centered care. Schedule your visit today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/appointment">
              <Button 
                data-testid="about-cta-appointment"
                className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-10 py-6 text-lg hover:bg-[#00c0cc] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
              >
                Book Appointment
              </Button>
            </Link>
            <Link to="/doctors">
              <Button 
                data-testid="about-cta-doctors"
                variant="outline"
                className="border-2 border-[#ff00ff] text-[#ff00ff] font-bold uppercase tracking-wider rounded-full px-10 py-6 text-lg hover:bg-[#ff00ff] hover:text-white transition-all"
              >
                Meet Our Team
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
